import {getEnvironment} from "../lib/get-environment";

/**
 * Identity Provider client config (per-environment).
 *
 * Provider-agnostic shape — Auth0 today, Zitadel migration is a config swap
 * plus an SDK swap inside `useIdpClient.ts`. The `provider` field disambiguates
 * provider-specific behavior at the SDK boundary.
 *
 * @module client/Auth/idp-config
 */

export type IdpProvider = 'auth0' | 'zitadel';

export interface IdpConfig {
    provider: IdpProvider;
    audience: string;
    domain: string;
    clientId: string;
    callbackUrl: string;
    afterLogout: string;
}

const IDP_CONFIG_DEV: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-dev.outlawpractice.com',
    clientId: 'ifJPp6HM7WuvhEYuoUSoPb62t16Y7hdF',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

const IDP_CONFIG_TEST: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-test.outlawpractice.com',
    clientId: 'CUIGoH4kOIShUfbifW7AKaDPHaUvkr8B',
    callbackUrl: 'https://app.outlawpractice.dev/callback',
    afterLogout: 'https://app.outlawpractice.dev/',
};

const IDP_CONFIG_DEMO: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-test.outlawpractice.com',
    clientId: 'CUIGoH4kOIShUfbifW7AKaDPHaUvkr8B',
    callbackUrl: 'https://app-demo.outlawpractice.com/callback',
    afterLogout: 'https://app-demo.outlawpractice.com/',
};

const IDP_CONFIG_BETA: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth.outlawpractice.com',
    clientId: 'cGMEchitYrYfM3uRMgBGvFIYtoOSc9ks',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

const IDP_CONFIG_PROD: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth.outlawpractice.com',
    clientId: 'cGMEchitYrYfM3uRMgBGvFIYtoOSc9ks',
    callbackUrl: 'https://app.outlawpractice.com/callback',
    afterLogout: 'https://app.outlawpractice.com/',
};

/**
 * Zitadel for the TS client against the Rust practice server (OWA-1137): the Outlaw Dev instance,
 * its Practice project (the access token's audience) and its "Practice Web" PKCE app. Development
 * and test share this one instance (John, 2026-10-12, q-20261012-225632-iuo0). The TS server stays
 * on Auth0, so this applies only when the client talks to the Rust server.
 */
const IDP_CONFIG_ZITADEL: IdpConfig = {
    provider: 'zitadel',
    audience: '394220269989789953',
    domain: 'auth-dev.outlaw.app',
    clientId: '394846861241876737',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

/** Development and test builds opt in to Zitadel with VITE_IDP_PROVIDER=zitadel (and VITE_API_HOST
 * pointing at the Rust server); every other build keeps its Auth0 entry. */
export function developmentProvider(env: Record<string, unknown> = import.meta.env): IdpProvider {
    return env.VITE_IDP_PROVIDER === 'zitadel' ? 'zitadel' : 'auth0';
}

let IDP_CONFIG: IdpConfig;

switch (getEnvironment()) {
    case 'P':
        IDP_CONFIG = IDP_CONFIG_PROD;
        break;
    case 'B':
        IDP_CONFIG = IDP_CONFIG_BETA;
        break;
    case 'DM':
        IDP_CONFIG = IDP_CONFIG_DEMO;
        break;
    case 'T':
        IDP_CONFIG = developmentProvider() === 'zitadel' ? IDP_CONFIG_ZITADEL : IDP_CONFIG_TEST;
        break;
    default:
        IDP_CONFIG = developmentProvider() === 'zitadel' ? IDP_CONFIG_ZITADEL : IDP_CONFIG_DEV;
}

export default IDP_CONFIG;

// Backward-compatible aliases (to be removed once all importers move to IdpConfig/IDP_CONFIG).
export type AuthConfig = IdpConfig;
